/**
 * Typed fetch wrapper for the Enable 2.0 staff API.
 *
 * - Base path is `/api/v1` (see docs/contracts/api-contracts.md). In dev the
 *   Vite proxy forwards `/api` to the FastAPI gateway on :8073.
 * - Every tenant-scoped request carries `X-Tenant-Id` (RLS, FND-003). For the
 *   demo build this defaults to the seeded hospital `hosp-001`.
 * - A bearer token (demo JWT) is attached when present.
 * - Errors are parsed from the standard envelope:
 *     { "error": { "code", "message", "details", "trace_id" } }
 *   and surfaced as a typed `ApiError`.
 */
import { API_V1 } from './types';

/**
 * Standard error envelope returned by the API (docs/contracts/api-contracts.md).
 * Declared locally so the wrapper does not couple to the exact field naming of
 * the shared package's error DTO; the wire shape is stable per the contract.
 */
interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown[];
    trace_id?: string;
  };
}

/** Default demo tenant. Overridable via VITE_TENANT_ID. */
export const DEMO_TENANT_ID = import.meta.env.VITE_TENANT_ID || 'hosp-001';

/** Base URL for the versioned API. */
const BASE_URL = API_V1;

/** Runtime error carrying the parsed envelope fields. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown[];
  readonly traceId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details: unknown[] = [],
    traceId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.traceId = traceId;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  const fallbackCode =
    res.status === 401
      ? 'UNAUTHORIZED'
      : res.status === 403
        ? 'FORBIDDEN'
        : res.status === 404
          ? 'NOT_FOUND'
          : 'ERROR';
  let body = '';
  try {
    body = await res.text();
  } catch {
    // ignore — use fallback below
  }
  if (body) {
    try {
      const parsed = JSON.parse(body) as ErrorEnvelope;
      const env = parsed.error;
      if (env) {
        return new ApiError(
          res.status,
          env.code ?? fallbackCode,
          env.message ?? res.statusText,
          env.details ?? [],
          env.trace_id
        );
      }
    } catch {
      // not JSON — fall through to raw body
    }
  }
  return new ApiError(res.status, fallbackCode, body || res.statusText);
}

export class ApiClient {
  private token: string | null = null;
  private tenantId: string;

  constructor(tenantId: string = DEMO_TENANT_ID) {
    this.tenantId = tenantId;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  setTenant(tenantId: string): void {
    this.tenantId = tenantId;
  }

  private headers(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': this.tenantId,
      ...extra,
    };
    if (this.token) {
      h['Authorization'] = `Bearer ${this.token}`;
    }
    return h;
  }

  /** Core request helper. Throws `ApiError` on non-2xx responses. */
  async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: this.headers(init?.headers as Record<string, string> | undefined),
    });
    if (!res.ok) {
      throw await parseError(res);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

/** Shared default client instance for app code that does not need its own. */
export const apiClient = new ApiClient();
